import { Theme } from '@radix-ui/themes';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import CommandMenu from '~/components/CommandMenu';
import PrivateRoute from '~/components/PrivateRoute';
import CheckAccess from '~/containers/CheckAccess';
import Navigation from '~/containers/Navigation';
import SiteLoading from '~/containers/SiteLoading';
import { FeatureProvider } from '~/context/feature';
import useAuthentication from '~/hooks/use-authentication';
import useOrganizations from '~/hooks/use-organizations';
import Login from '~/pages/Login';
import Logout from '~/pages/Logout';
import SignUp from '~/pages/SignUp';
import DebugRoutes from './DebugRoutes';
import PrivateRoutes from './PrivateRoutes';

const SetPassword = lazy(() => import(/* webpackChunkName: "setpassword" */ '~/pages/SetPassword'));

export const DEFAULT_PUBLIC_PATH = '/login';
export const DEFAULT_PRIVATE_PATH = '/dashboard';

function Routes() {
  const { isLoggedIn, usersCurrentOrganizationId, isLoading } = useAuthentication();
  const { isOrgGod } = useOrganizations();
  const history = useHistory();
  const location = useLocation();

  const [isLoginPage, setIsLoginPage] = useState(false);
  const [isLogoutPage, setIsLogoutPage] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    setIsLoginPage(path === '/login');
    setIsLogoutPage(path === '/logout');
  }, [location.pathname]);

  if (isLoading) {
    return <SiteLoading />;
  }

  if (isLoggedIn && !usersCurrentOrganizationId && !isLoginPage && !isLogoutPage) {
    return history.push('/login');
  }

  return (
    <Suspense fallback={<SiteLoading />}>
      <Switch>
        <Route path='/debug'>
          <DebugRoutes />
        </Route>

        <Route
          path='/'
          exact
        >
          {isLoggedIn && usersCurrentOrganizationId && !isOrgGod ? (
            <Redirect to={DEFAULT_PRIVATE_PATH} />
          ) : isOrgGod ? (
            <Redirect to='/sims' />
          ) : (
            <Redirect to={DEFAULT_PUBLIC_PATH} />
          )}
        </Route>

        <Route path='/sign-up'>
          <Theme
            appearance='light'
            style={{ backgroundColor: 'transparent' }} // needed this for the background video on sign-up to not be overriten by white background
          >
            <SignUp />
          </Theme>
        </Route>

        <Route path='/login'>
          <Login />
        </Route>

        <Route path='/logout'>
          <Logout />
        </Route>

        <Route path='/reset'>
          <SetPassword />
        </Route>

        <PrivateRoute to='/'>
          <CheckAccess>
            <FeatureProvider>
              <CommandMenu />
              <Navigation />
              <PrivateRoutes />
            </FeatureProvider>
          </CheckAccess>
        </PrivateRoute>

        <Route path='*'>
          <Redirect to={DEFAULT_PUBLIC_PATH} />
        </Route>
      </Switch>
    </Suspense>
  );
}

export default Routes;
